import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, CalendarDays, MapPin } from "lucide-react";
import { format, parseISO } from "date-fns";

function eventTypeLabel(t) {
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : "";
}
function formatDate(d) {
  try { return format(parseISO(d), "EEE, dd MMM yyyy"); } catch { return d; }
}
function availableCount(ev) {
  return (ev.assigned || []).filter((a) => a.status === "available").length;
}

export default function StaffingPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await api.get("/events");
      setEvents(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const understaffed = events
    .filter((ev) => ev.event_date >= today && availableCount(ev) < 2)
    .sort((a, b) => (a.event_date || "").localeCompare(b.event_date || ""));

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="staffing-page">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">Team Coverage</div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#2A2421] flex items-center gap-4">
          <UserPlus size={36} strokeWidth={1.5} className="text-[#D4A373]" /> Needs More Team
        </h1>
        <p className="text-[#5C5652] mt-4 max-w-xl">
          Upcoming events with fewer than 2 confirmed team members. Assign or await responses so every event is covered.
        </p>
      </div>

      {loading ? (
        <div className="text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>
      ) : understaffed.length === 0 ? (
        <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]" data-testid="no-understaffed">
          Every upcoming event has at least 2 confirmed team members. 
        </div>
      ) : (
        <div className="space-y-4" data-testid="understaffed-list">
          {understaffed.map((ev) => {
            const avail = availableCount(ev);
            const invited = (ev.assigned || []).filter((a) => a.status === "invited").length;
            return (
              <Link key={ev.id} to={`/events/${ev.id}`} data-testid={`understaffed-${ev.id}`}
                className="block p-6 bg-white border border-[#D4A373]/50 hover:border-[#D4A373] transition-colors">
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#C88270] mb-1">{eventTypeLabel(ev.event_type)}</div>
                    <div className="font-serif text-2xl tracking-tight text-[#2A2421]">{ev.couple_names || ev.title}</div>
                    <div className="text-sm text-[#5C5652] mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      <span className="flex items-center gap-1"><CalendarDays size={13} /> {formatDate(ev.event_date)} {ev.ready_by && `· ready by ${ev.ready_by}`}</span>
                      {ev.location && <span className="flex items-center gap-1"><MapPin size={13} /> {ev.location}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[#D4A373] font-medium text-lg">{avail}/2 confirmed</div>
                    {invited > 0 && <div className="text-xs text-[#5C5652]">{invited} awaiting response</div>}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
